import { type RefObject, useEffect, useState } from 'react'

import { getApiAccessToken } from '../../../auth/getApiAccessToken'
import { createLiveEmbedHost } from './liveEmbedHost'
import type { LiveEmbedState } from './liveEmbedProtocol'

/** Base URL of the live dashboards app; the feature is off when it is not set. */
export const LIVE_URL: string | undefined = import.meta.env.VITE_LIVE_URL || undefined

/** URL of a cloud dashboard in the live app: `embed` for an iframe of the Portal, without it for a new tab. */
export function liveDashboardUrl(siteId: string, dashboardId: string, { embed }: { embed: boolean }): string {
  const base = (LIVE_URL ?? '').replace(/\/+$/, '')
  const query = new URLSearchParams(embed ? { embed: '1', siteId } : { siteId })
  return `${base}/?${query}#/displaydashboard/${encodeURIComponent(dashboardId)}`
}

/**
 * Hands the Portal's API token to a live dashboards iframe and tracks the status it reports.
 * `src` must be the iframe's current URL so a new page load gets a fresh handshake.
 */
export function useLiveEmbed(iframeRef: RefObject<HTMLIFrameElement | null>, src: string | undefined): LiveEmbedState {
  const [state, setState] = useState<LiveEmbedState>({ status: 'loading', since: null })

  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe || !src || !LIVE_URL) return
    return createLiveEmbedHost({ iframe, liveUrl: LIVE_URL, getToken: getApiAccessToken, onState: setState })
  }, [iframeRef, src])

  return state
}
